import { KeyboardEvent, useEffect, useState } from 'react'
import { Autocomplete, Box, TextField } from '@mui/material'
import { Cancel as CancelIcon, Search as SearchIcon } from '@mui/icons-material'
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import { matchSorter } from 'match-sorter'
import { AppointmentProfile } from 'src/types'

export interface FilteredResults {
    filteredOptions?: AppointmentProfile[] | undefined
    selectedOption?: AppointmentProfile | undefined
}

interface AppointmentSearchAutocompleteProps {
    name: string
    label: string
    options: AppointmentProfile[]
    getOptionLabel: (option: string | AppointmentProfile) => string
    fullWidth?: boolean
    hiddenLabel?: boolean
    variant?: 'standard' | 'filled' | 'outlined'
    returnResults: (results: FilteredResults | undefined) => void
}

const AppointmentSearchAutocomplete = ({
    name,
    label,
    options,
    fullWidth = true,
    hiddenLabel = false,
    variant,
    getOptionLabel,
    returnResults
}: AppointmentSearchAutocompleteProps) => {
    const [filteredOptions, setFilteredOptions] = useState<
        AppointmentProfile[] | undefined
    >(undefined)

    const [inputValue, setInputValue] = useState<string>('')
    const [open, setOpen] = useState(false)

    const closeOptionList = () => setOpen(false)
    const openOptionList = () => setOpen(true)

    const manualFilterOptions = (
        options: AppointmentProfile[],
        inputValue: string
    ) => {
        if (!inputValue) {
            setFilteredOptions(undefined)
            returnResults(undefined)
        } else {
            const results = matchSorter(options, inputValue, {
                threshold: matchSorter.rankings.CONTAINS,
                keys: ['label', 'appointmentProfileTags.*.label', 'description']
            })
            setFilteredOptions(results)
            returnResults({ filteredOptions: results })
        }
    }

    useEffect(() => {
        manualFilterOptions(options, inputValue)
    }, [inputValue])

    const defaultOptionOnClickHandler = (
        option: AppointmentProfile | string
    ) => {
        if (typeof option !== 'string') {
            returnResults({
                filteredOptions,
                selectedOption: option
            })
        }
        if (document?.activeElement?.tagName === 'INPUT') {
            const e = document?.activeElement as HTMLInputElement
            e.blur()
        }
        closeOptionList()
    }

    return (
        <Box
            sx={{
                position: 'relative'
            }}
        >
            <SearchIcon
                color={'disabled'}
                sx={{ position: 'absolute', top: '16px', left: '14px' }}
            />
            <Autocomplete
                className="appointment-search-autocomplete"
                sx={{
                    '&.hasInputValue button.MuiButtonBase-root': {
                        visibility: 'visible'
                    },
                    '&.appointment-search-autocomplete label:not(.MuiInputLabel-shrink)':
                        {
                            pl: '32px'
                        },
                    '&.appointment-search-autocomplete label.MuiInputLabel-shrink':
                        {
                            pl: '0',
                            transition: '0.15s all'
                        },
                    'input': {
                        pl: '32px !important'
                    }
                }}
                classes={{
                    root: inputValue ? 'hasInputValue' : ''
                }}
                freeSolo
                open={open}
                value={inputValue}
                fullWidth={fullWidth}
                blurOnSelect={true}
                onChange={(event, value, reason) => {
                    if (
                        event?.type === 'click' &&
                        value === null &&
                        reason === 'clear'
                    ) {
                        setInputValue('')
                        returnResults(undefined)
                    }
                }}
                clearOnBlur={false}
                clearIcon={<CancelIcon />}
                options={filteredOptions ?? options}
                getOptionLabel={getOptionLabel}
                renderInput={params => (
                    <TextField
                        {...params}
                        label={label}
                        variant={variant}
                        hiddenLabel={hiddenLabel}
                        name={name}
                        onFocus={() => {
                            openOptionList()
                        }}
                        onBlur={() => {
                            closeOptionList()
                        }}
                        onChange={(event: any) => {
                            setInputValue(event.target.value)
                        }}
                    />
                )}
                onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    const element = event.target as HTMLInputElement
                    if (['Escape', 'Enter'].includes(event.key)) {
                        element.blur()
                        closeOptionList()
                    }
                }}
                onKeyUp={event => {
                    const element = event.target as HTMLInputElement
                    setInputValue(element.value ?? '')
                }}
                filterOptions={x => x}
                renderOption={(props, option, { inputValue }) => {
                    const { key, ...optionProps } = props
                    const label =
                        typeof option === 'string' ? option : option.label

                    optionProps.onClick = () =>
                        defaultOptionOnClickHandler(option)

                    const matches = match(label, inputValue, {
                        insideWords: true
                    })
                    const parts = parse(label, matches)

                    return (
                        <li key={key} {...optionProps}>
                            <div>
                                {parts.map((part, index) => (
                                    <span
                                        key={index}
                                        style={{
                                            color: part.highlight
                                                ? '#D81B60'
                                                : 'inherit'
                                        }}
                                    >
                                        {part.text}
                                    </span>
                                ))}
                            </div>
                        </li>
                    )
                }}
            />
        </Box>
    )
}

export default AppointmentSearchAutocomplete
